import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FiHome, FiMapPin } from "react-icons/fi";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import GuideSection from "../components/guide/GuideSection";
import { domiciliationGuide } from "../components/guide/guideContent";
import {
  CREATION_STEPS,
  PeriodPicker,
  PriceBlock,
  SummaryBar,
  billingFor,
  cartItemFor,
  showError,
  totalFor,
} from "../components/options/OptionParts";
import { addToCart, removeFromCart } from "../redux/actions/CartActions";
import {
  addToServiceList,
  removeFromServiceList,
} from "../redux/actions/ServiceListActions";
import {
  getCreationRegions,
  updateBusinessRequest,
} from "../redux/actions/RequestActions";
import pricing from "../assets/data/pricing.json";
import serviceCode from "../assets/data/serviceCode.json";
import { formatPrice } from "../utils/formatPrice";
import { isAssociation } from "../utils/creationFlow";
import "./styles/options.css";

const MIN_ADDRESS_LENGTH = 5;

function CompanySeat() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { services } = useSelector((state) => state.serviceList);
  const { businessCreationRequest } = useSelector((state) => state.request);

  const fromCart = new URLSearchParams(location.search).get("from") === "cart";
  // Associations give their own address, domiciliation is not offered
  const association = isAssociation(cart.businessType);

  // Choices are restored from the cart so the page can also be used to edit them
  const [seat, setSeat] = useState(() =>
    association
      ? "local"
      : cart?.domiciliation
        ? "domiciliation"
        : cart?.businessAddress
          ? "local"
          : null,
  );
  const [address, setAddress] = useState(cart?.businessAddress || "");
  const [region, setRegion] = useState(cart?.businessRegion || "");
  const [regions, setRegions] = useState([]);
  const [periodicity, setPeriodicity] = useState(
    cart?.domiciliation?.periodicity || "mensuel",
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const country =
    businessCreationRequest?.country || cart?.businessCountry || "CM";

  useEffect(() => {
    // Regions open to creation come first, the others stay below
    dispatch(
      getCreationRegions(country, (list) =>
        setRegions([...list].sort((a, b) => Number(b.open) - Number(a.open))),
      ),
    );
  }, [country, dispatch]);

  // A region saved earlier may have been closed since
  const regionIsOpen = regions.some(
    (item) => item.code === region && item.open,
  );

  const withDomiciliation = seat === "domiciliation";
  const addressIsValid = address.trim().length >= MIN_ADDRESS_LENGTH;
  const isValid =
    withDomiciliation || (seat === "local" && regionIsOpen && addressIsValid);

  const handleContinue = async () => {
    if (!isValid) return;
    setSubmitting(true);

    const domiciliationCode = serviceCode.domiciliation;
    const updatedServices = [
      ...services.filter((service) => service.code !== domiciliationCode),
      ...(withDomiciliation
        ? [
            {
              code: domiciliationCode,
              billing: billingFor(pricing.domiciliation, periodicity),
            },
          ]
        : []),
    ];

    const payload = { services: updatedServices };
    if (!withDomiciliation) {
      payload.address = address.trim();
      payload.region = region;
    }

    await dispatch(
      updateBusinessRequest(
        user.token,
        businessCreationRequest?.reference,
        payload,
        handleSaved,
      ),
    );
  };

  const handleSaved = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    if (withDomiciliation) {
      await dispatch(
        addToCart({
          domiciliation: cartItemFor(pricing.domiciliation, periodicity),
        }),
      );
      await dispatch(
        addToServiceList({
          code: serviceCode.domiciliation,
          billing: billingFor(pricing.domiciliation, periodicity),
        }),
      );
      await dispatch(removeFromCart("businessAddress"));
      await dispatch(removeFromCart("businessRegion"));
    } else {
      await dispatch(removeFromCart("domiciliation"));
      await dispatch(removeFromServiceList(serviceCode.domiciliation));
      await dispatch(
        addToCart({ businessAddress: address.trim(), businessRegion: region }),
      );
    }

    setSubmitting(false);
    navigate(fromCart ? "/cart" : "/options");
  };

  const label = !seat
    ? "Choisissez une option pour continuer"
    : seat === "local"
      ? !regionIsOpen
        ? "Choisissez la région pour continuer"
        : addressIsValid
          ? association
            ? "Adresse de l'association"
            : "Siège : votre local"
          : association
            ? "Saisissez l'adresse pour continuer"
            : "Saisissez l'adresse de votre local pour continuer"
      : "Domiciliation chez Platform98";

  const total = withDomiciliation
    ? `+ ${formatPrice(totalFor(pricing.domiciliation, periodicity))}`
    : "—";

  return (
    <div className="options-page">
      <Header />
      <ProgressBar steps={CREATION_STEPS} currentStep="Services" />

      <div className="container options-container">
        <header className="options-header">
          {association ? (
            <>
              <h1 className="options-title">
                Quelle est l'<span>adresse</span> de votre association ?
              </h1>
              <p className="options-lead">
                C'est l'adresse officielle de votre association, qui figurera
                dans son dossier de déclaration.
              </p>
            </>
          ) : (
            <>
              <h1 className="options-title">
                Où sera situé le <span>siège</span> de votre entreprise ?
              </h1>
              <p className="options-lead">
                Le siège social est l'adresse officielle de votre entreprise.
                Vous pouvez utiliser votre propre local ou vous domicilier chez
                nous.
              </p>
            </>
          )}
        </header>

        {!association && (
          <div
            className="seat-choices"
            role="radiogroup"
            aria-label="Siège social"
          >
            <button
              type="button"
              role="radio"
              aria-checked={seat === "local"}
              className={`seat-choice ${seat === "local" ? "is-selected" : ""}`}
              onClick={() => setSeat("local")}
            >
              <span className="option-icon" aria-hidden="true">
                <FiHome />
              </span>
              <span className="seat-choice-text">
                <strong>J'ai déjà un local</strong>
                <span>
                  Vous indiquez l'adresse de votre local professionnel.
                </span>
              </span>
              <span className="seat-radio" aria-hidden="true" />
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={withDomiciliation}
              className={`seat-choice ${withDomiciliation ? "is-selected" : ""}`}
              onClick={() => setSeat("domiciliation")}
            >
              <span className="option-icon" aria-hidden="true">
                <FiMapPin />
              </span>
              <span className="seat-choice-text">
                <strong>Je domicilie mon entreprise chez Platform98</strong>
                <span>Une adresse professionnelle, sans avoir de bureau.</span>
              </span>
              <span className="seat-radio" aria-hidden="true" />
            </button>
          </div>
        )}

        {seat === "local" && (
          <div className="seat-panel">
            <label htmlFor="business-region" className="seat-label">
              Région
            </label>
            <select
              id="business-region"
              className="seat-input"
              value={regionIsOpen ? region : ""}
              onChange={(event) => setRegion(event.target.value)}
            >
              <option value="">Choisissez la région</option>
              {regions.map((item) => (
                <option key={item.code} value={item.code} disabled={!item.open}>
                  {item.open ? item.name : `${item.name} — bientôt disponible`}
                </option>
              ))}
            </select>
            <p className="seat-hint">
              {association
                ? "Les autres régions seront bientôt disponibles."
                : "Votre local est dans une autre région ? Domiciliez votre entreprise chez Platform98."}
            </p>

            <label htmlFor="business-address" className="seat-label">
              {association
                ? "Adresse complète de l'association"
                : "Adresse complète de votre local"}
            </label>
            <input
              id="business-address"
              type="text"
              className="seat-input"
              placeholder="Ex. : Rue 1.234, Bonapriso, Douala"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              autoComplete="street-address"
              disabled={!regionIsOpen}
            />
            {address.length > 0 && !addressIsValid && (
              <p className="seat-error">
                Veuillez saisir une adresse complète.
              </p>
            )}
          </div>
        )}

        {withDomiciliation && (
          <div className="seat-panel">
            <p className="option-summary">
              Votre siège social est établi à une adresse professionnelle
              Platform98. Nous gérons votre courrier et vous accédez à nos
              espaces de travail.
            </p>
            <PeriodPicker value={periodicity} onChange={setPeriodicity} />
            <PriceBlock
              prices={pricing.domiciliation}
              periodicity={periodicity}
            />
          </div>
        )}
      </div>

      {!association && <GuideSection {...domiciliationGuide} />}

      <SummaryBar
        label={label}
        total={total}
        buttonLabel={
          submitting
            ? "Enregistrement…"
            : fromCart
              ? "Enregistrer et revenir au panier"
              : "Continuer"
        }
        disabled={submitting || !isValid}
        onContinue={handleContinue}
      />
    </div>
  );
}

export default CompanySeat;
